import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Logger } from '../../utils/logger';

const logger = new Logger('ErrorBoundary');

function ErrorFallback({ error, onReset }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#121212',
        color: '#fff',
        gap: 2,
        p: 4,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" sx={{ color: '#CF6679', fontWeight: 600 }}>
        Something went wrong
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 480 }}>
        An unexpected error occurred. Please reload the page. If the problem persists, contact support.
      </Typography>
      <Button
        variant="contained"
        onClick={() => window.location.reload()}
        sx={{ bgcolor: '#BB86FC', '&:hover': { bgcolor: '#9A67EA' }, mt: 1 }}
      >
        Reload Page
      </Button>
      {onReset && (
        <Button
          variant="text"
          onClick={onReset}
          sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}
        >
          Try to recover without reloading
        </Button>
      )}
    </Box>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error('Uncaught render error', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
